import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, LoadingOutlined, RobotOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, message, Progress, Space, Tag, Timeline, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../helpers/axiosConfig';
import './ModerationStatus.css';

const { Title, Text, Paragraph } = Typography;

const ModerationStatus = ({ petition }) => {
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Не запускаем таймер, если инициатива заблокирована или уже опубликована
    if (petition?.moderation_issues?.is_blocked || petition?.status === 'Опубликована') {
      setTimeLeft(null);
      setProgress(0);
      return;
    }

    // Используем time_left_seconds из moderation_status или вычисляем из will_be_published_at
    let initialSeconds = null;
    
    if (petition?.moderation_status?.time_left_seconds) {
      initialSeconds = petition.moderation_status.time_left_seconds;
    } else if (petition?.moderation_scheduled_at) {
      // Вычисляем время до начала модерации из moderation_scheduled_at
      const scheduledTime = new Date(petition.moderation_scheduled_at).getTime();
      const now = Date.now();
      initialSeconds = Math.max(0, Math.floor((scheduledTime - now) / 1000));
    }

    if (initialSeconds === null || initialSeconds <= 0) {
      setTimeLeft(0);
      setProgress(100);
      return;
    }

    const updateTimer = () => {
      const currentTimeLeft = Math.max(0, initialSeconds - Math.floor((Date.now() - mountTime) / 1000));
      
      setTimeLeft(currentTimeLeft);
      
      // Прогресс от начального времени до 0
      const progressPercent = Math.max(0, Math.min(100, ((initialSeconds - currentTimeLeft) / initialSeconds) * 100));
      setProgress(Math.round(progressPercent));
    };

    const mountTime = Date.now();
    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [petition]);

  // Показываем индикатор обновления каждые 10 секунд
  // НЕ обновляем, если инициатива заблокирована
  useEffect(() => {
    if (petition?.status === 'На модерации' && !petition?.moderation_issues?.is_blocked) {
      const interval = setInterval(() => {
        setIsRefreshing(true);
        setTimeout(() => setIsRefreshing(false), 1000);
      }, 10000);

      return () => clearInterval(interval);
    } else {
      // Останавливаем обновление, если заблокирована
      setIsRefreshing(false);
    }
  }, [petition?.status, petition?.moderation_issues?.is_blocked]);

  if (petition?.status !== 'На модерации') {
    return null;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}м ${secs}с`;
  };

      const handleDeleteAndRecreate = async () => {
        if (!petition?.id) {
          message.error(t('petition.notFound'));
          return;
        }

        setIsDeleting(true);
        
        try {
          // Получаем client_id из петиции или localStorage
          const clientId = petition.client_id || localStorage.getItem('anonymous_client_id') || null;
          
          if (!clientId) {
            message.error(t('petition.deleteError'));
            setIsDeleting(false);
            return;
          }
          
          // ШАГ 1: Если у петиции нет client_id, сначала обновляем её
          if (!petition.client_id) {
            try {
              // Пробуем обновить через PATCH запрос
              await api.patch(`/voice-petition/${petition.id}/`, {
                client_id: clientId
              }, {
                headers: {
                  'X-Client-Id': clientId
                }
              });
            } catch (updateError) {
              // Пробуем через заголовок
              try {
                await api.patch(`/voice-petition/${petition.id}/`, {}, {
                  headers: {
                    'X-Client-Id': clientId
                  }
                });
              } catch (updateError2) {
                // Продолжаем попытку удаления - бэкенд сам обновит при необходимости
              }
            }
          }
          
          // ШАГ 2: Удаляем петицию
          let deleted = false;
          let lastError = null;
          
          // Пробуем разные способы передачи client_id для удаления
          // Вариант 1: DELETE с client_id в заголовке X-Client-Id
          try {
            await api.delete(`/voice-petition/${petition.id}/`, {
              headers: {
                'X-Client-Id': clientId
              }
            });
            deleted = true;
          } catch (errorHeader) {
            
            // Вариант 2: DELETE с client_id в теле запроса
            try {
              await api.delete(`/voice-petition/${petition.id}/`, {
                data: { client_id: clientId }
              });
              deleted = true;
            } catch (errorBody) {
              
              // Вариант 3: DELETE с client_id в query параметре
              try {
                await api.delete(`/voice-petition/${petition.id}/?client_id=${encodeURIComponent(clientId)}`);
                deleted = true;
              } catch (errorQuery) {
                lastError = errorQuery;
              }
            }
          }
          
          // Резервные варианты (если новый endpoint не сработал)
          if (!deleted) {
            // Пробуем через /mypetition/{id}/ (требует авторизации)
            try {
              await api.delete(`/mypetition/${petition.id}/`);
              deleted = true;
            } catch (error3) {
              if (!lastError) {
                lastError = error3;
              }
            }
          }

      if (deleted) {
        message.success(t('petition.deleteErrorGeneric'));
        // Перенаправляем на страницу создания голосом
        setTimeout(() => {
          navigate('/voice');
        }, 500);
      } else {
        // Проверяем тип ошибки
        const status = lastError?.response?.status;
        
        if (status === 404) {
          message.error({
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>{t('petition.deleteErrorGeneric')}</strong>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {t('petition.deleteUnavailable')}
                </div>
                <div style={{ marginTop: 8 }}>
                  <Button 
                    type="link" 
                    onClick={() => navigate('/login')}
                    style={{ padding: 0 }}
                  >
                    {t('petition.goToAuth')}
                  </Button>
                </div>
              </div>
            ),
            duration: 10
          });
        } else if (status === 401 || status === 403) {
          if (clientId) {
            message.error({
              content: (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>{t('petition.deleteErrorGeneric')}</strong>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {t('petition.deleteUnavailable')}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button 
                      type="link" 
                      onClick={() => navigate('/login')}
                      style={{ padding: 0 }}
                    >
                      {t('petition.goToAuth')}
                    </Button>
                  </div>
                </div>
              ),
              duration: 10
            });
          } else {
            message.error(t('petition.deleteError'));
          }
        } else {
          const errorMessage = lastError?.response?.data?.detail || 
                              lastError?.response?.data?.message || 
                              lastError?.message || 
                              'Неизвестная ошибка';
          message.error(`${t('petition.deleteErrorGeneric')}: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error deleting petition:', error);
      if (error?.response?.status === 401) {
        message.error(t('petition.deleteError'));
      } else {
        message.error(t('petition.deleteErrorGeneric'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const moderationStatus = petition.moderation_status;
  const issues = petition.moderation_issues;

  return (
    <Card 
      className="moderation-status-card"
      style={{ marginBottom: 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        
        {/* Заголовок - убираем, оставляем только Timeline */}

        {/* Прогресс бар - показываем только если идет AI проверка (есть таймер) */}
        {timeLeft !== null && timeLeft > 0 && !issues?.is_blocked && petition?.status === 'На модерации' && (
          <div style={{ marginBottom: 16 }}>
            <Progress 
              percent={progress} 
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              format={(percent) => `${percent}%`}
            />
          </div>
        )}

        {/* Timeline процесса - показываем только если идет ожидание AI проверки (есть таймер) */}
        {timeLeft !== null && timeLeft > 0 && !issues?.is_blocked && (
          <Timeline
            items={[
              {
                color: 'green',
                dot: <CheckCircleOutlined />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{t('moderation.created')}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {t('moderation.createdDesc')}
                    </Text>
                  </div>
                ),
              },
              {
                color: 'orange',
                dot: <ClockCircleOutlined />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{t('moderation.aiCheck')}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {`${t('moderation.aiCheckStartIn')} ${formatTime(timeLeft)}`}
                    </Text>
                  </div>
                ),
              },
              {
                color: 'gray',
                dot: <ClockCircleOutlined />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{t('moderation.publication')}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {t('moderation.publicationPending')}
                    </Text>
                  </div>
                ),
              },
            ]}
          />
        )}

        {/* Если AI проверка завершена и петиция на модерации - показываем только статус */}
        {(timeLeft === null || timeLeft === 0) && !issues?.is_blocked && petition?.status === 'На модерации' && (
          <Alert
            message={t('moderation.aiCheckCompleted')}
            description={t('moderation.publicationPending')}
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
          />
        )}

        {/* Проблемы если есть - показываем всегда когда есть issues или is_blocked */}
        {(issues?.has_issues || issues?.is_blocked || issues?.issues?.length > 0 || issues?.violated_laws?.length > 0) && (
          <Alert
            message={issues?.is_blocked ? t('moderation.blocked') : t('moderation.issuesFound')}
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                {issues.severity && (
                  <div>
                    <Text strong>{t('moderation.severity')} </Text>
                    <Tag color={issues.severity === 'high' ? 'red' : issues.severity === 'medium' ? 'orange' : 'blue'}>
                      {issues.severity === 'high' ? t('moderation.severityHigh') : issues.severity === 'medium' ? t('moderation.severityMedium') : t('moderation.severityLow')}
                    </Tag>
                  </div>
                )}
                
                {issues.issues?.length > 0 && (
                  <div>
                    <Text strong>{t('moderation.problems')}</Text>
                    <ul style={{ marginTop: 8, marginBottom: 8 }}>
                      {issues.issues.map((issue, index) => (
                        <li key={index}>
                          <Text>{issue}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {issues.violated_laws?.length > 0 && (
                  <div>
                    <Text strong>{t('moderation.violatedLaws')}</Text>
                    <ul style={{ marginTop: 8, marginBottom: 8 }}>
                      {issues.violated_laws.map((law, index) => (
                        <li key={index}>
                          <Text type="danger">{law}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {issues.recommendation && (
                  <div style={{ marginTop: 12 }}>
                    <Paragraph style={{ margin: 0, fontStyle: 'italic' }}>
                      💡 {issues.recommendation}
                    </Paragraph>
                  </div>
                )}

                {issues.is_blocked && (
                  <div style={{ marginTop: 12 }}>
                    <Text>{t('moderation.blockedDesc')}</Text>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={handleDeleteAndRecreate}
                      loading={isDeleting}
                      danger
                      style={{ marginTop: 8 }}
                    >
                      {t('moderation.recreate')}
                    </Button>
                  </div>
                )}
              </Space>
            }
            type={issues?.is_blocked || issues?.severity === 'high' ? 'error' : 'warning'}
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

      </Space>
    </Card>
  );
};

export default ModerationStatus;
